import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { CONFIG } from '@/config-globals';
import { useToast } from '@/hooks/use-toast';
import { formatDateOnly } from '@/lib/date';
import { applyBestMask } from '@/lib/mask';
import { formatSizeBreakdown } from '@/lib/sale-item-sizes';
import { isSaleEditable } from '@/lib/sale-status';
import { useDynamicList } from '@/services/dynamic-resource';
import { StatusBadge } from './status';

interface SaleDetailProps {
  sale: any;
  items: any[];
  onEdit?: () => void;
  /** Só usado no preview rápido (modal do Kanban) — leva pra tela cheia de visualização. */
  onView?: () => void;
}

async function downloadFile(endpoint: string, filename: string) {
  const res = await fetch(endpoint);
  if (!res.ok) throw new Error('Falha ao gerar PDF');

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function money(value: number) {
  return (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function itemDescription(item: any) {
  return [
    item.shirtType?.description,
    item.sleeveType?.description,
    item.collarType?.description,
    item.fabricType?.description,
    item.printType?.description,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Corpo do pedido — usado tanto na tela de visualização quanto no modal do
 * Kanban e na impressão. `print:` cuida do visual quando sai pela impressora
 * (window.print()); a página/modal que envolve isso decide o que mais some.
 */
export function SaleDetail({ sale, items, onEdit, onView }: SaleDetailProps) {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const [downloadingCuttingMap, setDownloadingCuttingMap] = useState(false);
  const [sendingPdf, setSendingPdf] = useState(false);
  const { data: optionsResponse } = useDynamicList('additional-options', {
    perPage: 200,
  });
  const options: any[] = optionsResponse?.data ?? [];
  const optionLabel = (id: string) => options.find((o) => o.id === id)?.label ?? id;

  const depositAmount = sale.depositAmount ?? 0;
  const balanceAmount = (sale.total ?? 0) - depositAmount;
  const invoiceOption = options.find((o) => o.slug === 'emitir-nota-fiscal');
  const issuesInvoice =
    invoiceOption && (sale.selectedOptions ?? []).includes(invoiceOption.id);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile(
        `/api/v1/sales/${sale.id}/pdf`,
        `pedido-${sale.saleNumber ?? sale.id.slice(0, 8)}.pdf`,
      );
    } catch {
      toast.error('Não foi possível gerar o PDF. Tente novamente.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendPdf = async () => {
    setSendingPdf(true);
    try {
      const res = await fetch(`/api/v1/sales/${sale.id}/send-pdf`, { method: 'POST' });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message ?? 'Falha ao enviar PDF');
      toast.success('PDF enviado ao cliente pelo WhatsApp.');
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Não foi possível enviar o PDF ao cliente. Tente novamente.',
      );
    } finally {
      setSendingPdf(false);
    }
  };

  const handleDownloadCuttingMap = async () => {
    setDownloadingCuttingMap(true);
    try {
      await downloadFile(
        `/api/v1/sales/${sale.id}/cutting-map`,
        `mapa-de-corte-${sale.saleNumber ?? sale.id.slice(0, 8)}.pdf`,
      );
    } catch {
      toast.error('Não foi possível gerar o Mapa de Corte. Tente novamente.');
    } finally {
      setDownloadingCuttingMap(false);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between border-b-4 border-primary pb-4 mb-6">
        <div>
          <div className="text-2xl font-bold">{CONFIG.APP_NAME}</div>
          <div className="text-muted-foreground text-sm">
            Pedido {sale.saleNumber ?? `#${sale.id.slice(0, 8)}`}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={sale.status} />
          <div className="flex gap-2">
            {onView && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                onClick={onView}
              >
                <Icon name="eye" /> Visualizar
              </Button>
            )}
            {onEdit && isSaleEditable(sale.status) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                onClick={onEdit}
              >
                <Icon name="pencil" /> Editar
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              disabled={downloading}
              onClick={handleDownload}
            >
              <Icon name="download" /> {downloading ? 'Gerando...' : 'Baixar PDF'}
            </Button>
            {sale.customer?.phone && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                disabled={sendingPdf}
                onClick={handleSendPdf}
              >
                <Icon name="send" />{' '}
                {sendingPdf ? 'Enviando...' : 'Enviar PDF ao cliente'}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              disabled={downloadingCuttingMap}
              onClick={handleDownloadCuttingMap}
            >
              <Icon name="scissors" />{' '}
              {downloadingCuttingMap ? 'Gerando...' : 'Mapa de Corte'}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <InfoTile label="Cliente" value={sale.customer?.name ?? '—'} />
        <InfoTile
          label="CPF/CNPJ"
          value={
            sale.customer?.documentNumber
              ? applyBestMask(sale.customer.documentNumber, [
                  '999.999.999-99',
                  '99.999.999/9999-99',
                ])
              : '—'
          }
        />
        <InfoTile
          label="Telefone"
          value={
            sale.customer?.phone
              ? applyBestMask(sale.customer.phone, ['(99) 9999-9999', '(99) 99999-9999'])
              : '—'
          }
        />
        <InfoTile
          label="Forma de Pagamento"
          value={sale.paymentMethod?.description ?? '—'}
        />
        <InfoTile label="Entrega" value={formatDateOnly(sale.deliveryDate)} />
      </div>

      <div className="overflow-x-auto mb-6">
        <table className="w-full text-sm border-collapse min-w-[560px]">
          <thead>
            <tr className="bg-primary text-primary-foreground print:bg-neutral-800 print:text-white">
              <th className="text-left font-semibold p-2 rounded-l-md print:rounded-none">
                Composição
              </th>
              <th className="text-center font-semibold p-2 w-20">Qtd.</th>
              <th className="text-right font-semibold p-2 w-28">Unitário</th>
              <th className="text-right font-semibold p-2 w-28 rounded-r-md print:rounded-none">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr
                key={item.id}
                className={index % 2 === 0 ? 'bg-muted/40 print:bg-neutral-100' : ''}
              >
                <td className="p-2 border-l-4 border-primary print:border-black">
                  <div>{itemDescription(item)}</div>
                  <div className="text-xs mt-0.5">
                    {formatSizeBreakdown(item.sizeBreakdown) ?? (
                      <span className="text-amber-600 font-medium">Tamanho pendente</span>
                    )}
                  </div>
                  <div className="flex gap-1 mt-1">
                    {(item.selectedOptions ?? []).map((id: string) => (
                      <Badge key={id} variant="secondary" className="text-[10px]">
                        {optionLabel(id)}
                      </Badge>
                    ))}
                    {item.artworkPath && (
                      <Badge variant="secondary" className="text-[10px]">
                        Arte anexada
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="p-2 text-center">{item.quantity}</td>
                <td className="p-2 text-right">{money(item.unitaryPrice)}</td>
                <td className="p-2 text-right font-medium">{money(item.totalPrice)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <div className="w-full max-w-xs space-y-1">
          {(sale.operationalCost ?? 0) > 0 && (
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Custo Operacional</span>
              <span>{money(sale.operationalCost)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Entrada {sale.depositPaidAt ? '(pago)' : '(pendente)'}</span>
            <span>{money(depositAmount)}</span>
          </div>
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Saldo {sale.balancePaidAt ? '(pago)' : '(pendente)'}</span>
            <span>{money(balanceAmount)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold border-t-2 border-primary pt-2 print:border-black">
            <span>Total</span>
            <span>{money(sale.total)}</span>
          </div>
        </div>
      </div>

      {sale.budgetNotes && (
        <div className="mt-4 p-3 border rounded-md bg-muted/40 text-sm whitespace-pre-wrap">
          <div className="font-semibold mb-1">
            Dados coletados no orçamento (WhatsApp)
          </div>
          {sale.budgetNotes}
        </div>
      )}

      {issuesInvoice && (
        <div className="mt-4 text-sm text-muted-foreground">
          Nota Fiscal: <strong>{sale.invoiceNumber ?? 'não informada'}</strong>
        </div>
      )}

      <div className="hidden print:block mt-10 pt-4 border-t text-xs text-neutral-500 text-center">
        Impresso em {new Date().toLocaleString('pt-BR')} · {CONFIG.APP_NAME}
      </div>
    </div>
  );
}

function InfoTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="border rounded-md p-2 print:border-neutral-400">
      <div className="text-xs text-muted-foreground print:text-neutral-600">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
